import { useState, type FormEvent } from 'react'
import { api, type ApiError } from '../lib/api'
import { navigate } from '../lib/router'
import { AuthLayout } from '../components/auth/AuthLayout'
import { FloatingInput } from '../components/ui/input'
import { Button, ButtonLink } from '../components/ui/button'
import { StatusBox } from '../components/ui/badge'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await api.requestPasswordReset(email)
      setSent(true)
    } catch (err) {
      setError((err as ApiError).title ?? 'Failed to send reset email')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle={sent ? undefined : "Enter your email and we'll send you a link to reset your password."}
      panel={{ line1: 'Locked out?', line2: 'A reset link is one email away.' }}
      footer={
        sent ? undefined : (
          <p className="text-center">
            Remember your password?{' '}
            <ButtonLink
              variant="link"
              size="sm"
              className="h-auto px-0 text-sm"
              href="/login"
              onClick={(e) => { e.preventDefault(); navigate('/login') }}
            >
              Log in
            </ButtonLink>
          </p>
        )
      }
    >
      {sent ? (
        <div className="space-y-4">
          <StatusBox tone="success">
            If an account with that email exists, we've sent a reset link. Check your inbox.
          </StatusBox>
          <Button size="auth" onClick={() => navigate('/login')}>
            Back to login
          </Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit}>
          <FloatingInput
            label="Email"
            type="email"
            size="auth"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
          />

          {error && <StatusBox tone="destructive">{error}</StatusBox>}

          <Button type="submit" size="auth" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
